<template>
  <div
    ref="componentRoot"
    :class="{
      'product-list-view-fi': true,
      [`product-list-view-fi--${shopType}`]: true,
      [`product-list-view-fi--max-${maxHeadings}`]: true,
      [`product-list-view-fi--with-variant-image`]: showVariantImage,
      [`product-list-view-fi--with-memo-list`]: showMemoListButton,
      ...headerHeightClasses,
    }"
  >
    <div
      ref="productListFilter"
      class="product-list-view-fi__filter"
    >
      <facets-menu-fi
        v-if="listFacets?.length"
        ref="facetsMenu"
        :facets="listFacets"
        :allow-clear-all="!autoFilters"
        class="product-list-view-fi__facets"
        clip
        @change="changeFacet"
        @clear-all-facets="clearAllFacets"
        @mobile-facet-closed="jumpToProductList"
      >
        <template #facets="{ facet, open, change, close, toggleOpen, idx, registerDomElement }">
          <fact-finder-facet-fi
            :facet="facet"
            :open="open"
            :idx="idx"
            :register-dom-element="registerDomElement"
            @change="change"
            @toggle-open="toggleOpen(facet)"
            @close="close(facet)"
          />
        </template>
        <template #selection="{ facet, element }">
          {{ cleanName(facet.name) }}:
          <span
            v-if="facet.type === 'FLOAT' || facet.type === 'INTEGER'"
            class="facets-selection-fi__value"
          >
            {{ getLocalizedValue(element.selectedMinValue) }}
            [{{ facet.unit }}] -
            {{ getLocalizedValue(element.selectedMaxValue) }}
            [{{ facet.unit }}]
          </span>
          <span
            v-else
            class="facets-selection-fi__value"
          >
            {{ element.text }}
          </span>
        </template>
      </facets-menu-fi>
    </div>
    <div
      v-if="!isFite"
      v-intersect="onIntersect"
      class="fi-relative fi-bottom-[80px]"
    />
    <template v-if="searchProductsForFamilyNg?.totalHits !== 0">
      <div
        class="product-list-view-fi__wrapper"
        :class="`shop-type-${shopType}`"
        data-test="product-list-view-wrapper"
        :data-test-2="shopType === 'retailConnect' ? 'product-list-view-retail-connect' : null"
      >
        <div
          v-if="!isFite && listFacets?.length"
          :class="{
            'product-list-view-fi__filter-hint': true,
            'product-list-view-fi__filter-hint--visible': showFilterBadge,
            'product-list-view-fi__filter-hint--active': activeFacets.length,
          }"
        >
          <div
            class="product-list-view-fi__filter-hint__badge"
            @click="goToFilter"
          >
            <div
              v-if="activeFacets.length"
              class="product-list-view-fi__filter-hint__text"
            >
              {{
                $dict.get('search.Fact-Finder.Search-Result.Filter-Active', [
                  String(activeFacets.length),
                ])
              }}
            </div>
            <div
              v-else
              class="product-list-view-fi__filter-hint__text"
            >
              {{ $dict.get('search.Fact-Finder.Search-Result.Filter') }}
            </div>
            <icon-fi
              icon="filter"
              class="product-list-view-fi__filter-hint__icon"
            />
          </div>
        </div>

        <div v-if="isLoading || isProductIsLoading">
          <loading-indicator
            variant="spinnerLocal"
            class="spinner-local--rebrush"
            :dark="true"
          />
        </div>

        <template v-else>
          <div class="product-list-view-fi__attribute-toggle">
            <product-attributes-toggle-fi
              v-if="hasApprovalAttributes"
              is-block
            />
          </div>

          <product-list-head-fi
            :total-hits="searchProductsForFamilyNg.totalHits"
            :catalog-attributes="getAttributes(paddedAttributes[products[0]?.ID])"
            :show-variant-image="showVariantImage"
          />
        </template>

        <accordion-customizable-wrapper-fi theme="plist">
          <accordion-customizable-fi
            v-for="(item, i) in products"
            :id="`accordion_${item.ID}`"
            :key="item.ID"
            :item-index="i"
            :shop-type="shopType ? shopType : 'default'"
            :label="item.catalogProductData.ProductName"
            :scroll-to="!isLG"
            theme="plist"
            data-test="accordion-customizable-item"
          >
            <template #headline>
              <div class="product-list-view-fi__header">
                <grid-container-fi
                  :container="false"
                  class="product-list-view-fi__header-main"
                  :grid-class="shopType !== 'closedShop' ? 'md:fi-gap-y-zero md:fi-gap-y-zero' : ''"
                >
                  <product-identifier-fi
                    :item="item"
                    :best-price="bestPrices[item.ID] ?? undefined"
                    :click-source="clickSource ?? undefined"
                    :show-variant-image="showVariantImage"
                  />

                  <product-attributes-fi
                    class="fi-hidden sm:fi-block"
                    :attributes="getAttributes(paddedAttributes[item.ID])"
                  />

                  <product-list-closed-shop-fi
                    v-if="isMounted && shopType === 'closedShop'"
                    :best-price="bestPrices[item.ID] ?? undefined"
                    :item="item"
                    :loading-prices="loadingPrices"
                    :is-adding-to-cart="isAddingItemToCart[item.ID]"
                    :show-memo-list-button="showMemoListButton"
                    @remove-quantity-hints="removeQuantityHints"
                    @logout-if-token-invalid="logoutIfTokenInvalid"
                    @add-product="(productDetails) => addProduct(productDetails)"
                    @add-to-memo-list="openMemoListDialog"
                  />

                  <!-- RetailConnectCart -->
                  <template
                    v-else-if="
                      shopType === 'retailConnectCart' && !isSystemItem(item.catalogProductData)
                    "
                  >
                    <product-buy-button-fi
                      :item="item"
                      :product-details="{
                        productID: item.ID,
                        productName: `${item.ID} - ${item.catalogProductData.ProductName}`,
                        productInitialQuantity: 1,
                        unit: bestPrices[item.ID]?.price?.unit ?? undefined,
                      }"
                      :is-in-progress="isAddingItemToCart[item.ID] || retailerAvailabilityLoading"
                      :is-button-disabled="
                        productInCart(item) ||
                        isAddingItemToCart[item.ID] ||
                        retailerOutOfStock[i] ||
                        false
                      "
                      :is-button-hidden="hasAvailabilityStatusOnlyPermission"
                      @add-product="(productDetails) => addProduct(productDetails)"
                    />
                  </template>
                </grid-container-fi>

                <grid-container-fi
                  v-if="item.quantityIncreasedMessage"
                  :container="false"
                  class="product-list-view-fi__header-hint"
                >
                  <quantity-modification-hint-fi
                    class="product-list-view-fi__quantity-modification-hint"
                    :hint="item.quantityIncreasedMessage"
                    icon="info"
                    theme="product-list-view"
                  />
                </grid-container-fi>
              </div>
            </template>

            <template #content>
              <product-content-fi
                :item="item"
                :attributes="paddedAttributes[item.ID]"
                :family-line-drawing="familyLineDrawing"
                :best-price="bestPrices[item.ID] ?? undefined"
              />
            </template>
          </accordion-customizable-fi>
        </accordion-customizable-wrapper-fi>

        <div class="fi-hidden md:fi-block">
          <product-list-legend-fi v-if="showLegend" />
        </div>
      </div>

      <pagination-progress-bar
        v-if="searchProductsForFamilyNg?.totalHits"
        :pagination-page="pagination.page"
        :pagination-total="searchProductsForFamilyNg.totalHits || 0"
        :progressbar-value="pagingPercentage"
        :show-more-button="displayedItems < searchProductsForFamilyNg.totalHits"
        :query-params="filterUrlParams"
        :disabled-state="
          isProductIsLoading ||
          pagination.page * pagination.pageSize >= searchProductsForFamilyNg.totalHits
        "
        @loadmore="fetchMoreProducts"
      >
        <template
          v-if="pagination.page * pagination.pageSize <= searchProductsForFamilyNg.totalHits"
        >
          <span>{{
            $dict.get('product-List.Pagination.Show-Variants', [
              pagination.page * pagination.pageSize,
              searchProductsForFamilyNg.totalHits,
            ])
          }}</span>
        </template>

        <template
          v-else-if="pagination.page * pagination.pageSize > searchProductsForFamilyNg.totalHits"
        >
          <span>{{
            $dict.get('product-List.Pagination.Show-Variants', [
              searchProductsForFamilyNg.totalHits,
              searchProductsForFamilyNg.totalHits,
            ])
          }}</span>
        </template>
      </pagination-progress-bar>
    </template>
    <no-products-found-fi v-else />
    <div
      v-if="!isFite"
      v-intersect="onIntersectFilterOut"
      class="fi-relative fi-bottom-[300px]"
    />

    <add-to-memo-list-dialog-fi
      v-if="showMemoListButton"
      ref="addToMemoListDialog"
      :product="memoListProduct"
      :quantity="memoListQuantity"
      multiple
      @error-occured="onMemoListError"
    />
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import {
  AccordionCustomizableFi,
  AccordionCustomizableWrapperFi,
  IconFi,
  LoadingIndicator,
  PaginationProgressBar,
  QuantityModificationHintFi,
} from 'atoms';
import { storeToRefs } from 'pinia';
import {
  computed,
  defineAsyncComponent,
  inject,
  nextTick,
  onMounted,
  onServerPrefetch,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from 'vue';

import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import AddToMemoListDialogFi from '@/components/memo-list/add-to-memo-list-dialog-fi/AddToMemoListDialogFi.vue';
import FactFinderFacetFi from '@/components/search-fact-finder-fi/fact-finder-facet-fi/FactFinderFacetFi.vue';
import factFinderNgNameCleaner from '@/components/search-fact-finder-fi/factFinderNgNameCleaner';
import mergeFacets from '@/components/search-fact-finder-fi/mergeFacets';
import stringifyFilterValue from '@/components/search-fact-finder-fi/stringifyFilterValue';
import toFilterInput from '@/components/search-fact-finder-fi/toFilterInput';
import { useAccessPrivileges } from '@/composables/accessPrivileges';
import { useAutoFiltersComposable } from '@/composables/autoFilters';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import { useHeaderHeight } from '@/composables/headerHeight';
import { useLocalizedValue } from '@/composables/localizedValue';
import { useBestPrices } from '@/composables/multiBestPrice';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import { type AddProductToCartParameters, useShoppingcart } from '@/composables/shoppingcart';
import { useSmoothScroll } from '@/composables/smoothScroll';
import { useSystemPartner } from '@/composables/systemPartner';
import { useMemoListFunction } from '@/composables/useMemoList';
import { useUser } from '@/composables/user';
import GET_FAMILY_BASE_FACETS_NG from '@/graphql/getFamilyBaseFacetsNg.gql';
import GET_RETAILER_AVAILABILITY_FOR_IDS from '@/graphql/getRetailerAvailabilityForIds.gql';
import SEARCH_PRODUCTS_FOR_FAMILY_NG from '@/graphql/searchProductsForFamilyNg.gql';
import { useNotificationStore } from '@/store/pinia/notification';
import { useProductFamilyStore } from '@/store/pinia/product-family';
import { useProductListStore } from '@/store/pinia/product-list';

import padAttributes from './padAttributes';
import ProductAttributesFi from './product-attributes-fi/ProductAttributesFi.vue';
import ProductAttributesToggleFi from './product-attributes-toggle-fi/ProductAttributesToggleFi.vue';
import ProductBuyButtonFi from './product-buy-button-fi/ProductBuyButtonFi.vue';
import ProductContentFi from './product-content-fi/ProductContentFi.vue';
import ProductIdentifierFi from './product-identifier-fi/ProductIdentifierFi.vue';
import ProductListHeadFi from './product-list-head-fi/ProductListHeadFi.vue';
import ProductListLegendFi from './product-list-legend-fi/ProductListLegendFi.vue';

const NoProductsFoundFi = defineAsyncComponent(() => import('./NoProductsFoundFi.vue'));
const ProductListClosedShopFi = defineAsyncComponent(
  () => import('./product-list-closed-shop-fi/ProductListClosedShopFi.vue'),
);

defineOptions({ name: 'ProductListViewFi' });

const props = withDefaults(
  defineProps<{
    shopType?: string;
    devData?: Record<string, any> | null;
    familyLineDrawing?: string | null;
    approvals?: any[];
    catalogData?: Record<string, any> | null;
    clickSource?: string | null;
  }>(),
  {
    shopType: '',
    devData: null,
    familyLineDrawing: null,
    approvals: () => [],
    catalogData: null,
    clickSource: null,
  },
);

const emit = defineEmits<{ ready: [] }>();

const fields = inject<Record<string, any>>('fields', {});

const fischer = useFischer();

const componentRoot = useTemplateRef('componentRoot');
const { smoothScrollToElement } = useSmoothScroll(componentRoot);

const productFamilyStore = useProductFamilyStore();
const { setFamilyFacets } = productFamilyStore;
const { activeFacets, listFacets, loading, totalHits, familyProducts } =
  storeToRefs(productFamilyStore);
const productListStore = useProductListStore();
const { openGeneralErrorNotification } = useNotificationStore();

const { headerHeightClasses } = useHeaderHeight();
const { hasAvailabilityStatusOnlyPermission } = useAccessPrivileges();
const { withSalesChannelFilter, supplier, isPublicAssortment } = useSalesChannelFilter();
const { isSM, isLG } = useBreakpoints();
const { getLocalizedValue } = useLocalizedValue();
const { isSystemItem } = useSystemPartner();

const { logoutIfTokenInvalid } = useUser();
const { cart, getCart, isAddingItemToCart, addProductToCart } = useShoppingcart({
  logoutIfTokenInvalid,
});

const pagination = reactive({
  page: 1,
  pageSize: 18,
  total: 0,
  initial: 1,
});
const isLoading = ref(true);
const count = ref(0);
const showLegend = ref(false);
const isMounted = ref(false);
const showFilterBadge = ref(false);
const observersReady = ref(false);
const isFilterOutOfView = ref(false);
const initDone = ref(false);

const filters = ref<any[]>([]);
const filtersRef = computed({
  get: () => filters.value,
  set: (value: any[]) => {
    filters.value = value;
  },
});
const { applyChanges, autoFilters, enableAutoFilters, useAutoFilters } =
  useAutoFiltersComposable(filtersRef);

const maxHeadings = computed(() => productListStore.maxHeadings);

const products = computed({
  get: () => productListStore.products,
  set: (value) => productListStore.setProducts(value),
});
const loadingQuantityInputs = computed({
  get: () => productListStore.loadingQuantityInputs,
  set: (value) => productListStore.setLoadingQuantityInputs(value),
});
const approvalsEnabled = computed({
  get: () => productListStore.approvalsEnabled,
  set: (value) => productListStore.setApprovalsEnabled(value),
});
const baseSiteId = computed(() => {
  if (IS_SHOWROOM && props.devData) return props.devData.baseSiteId;
  return fischer.baseSiteId();
});

const isRetailConnectCart = computed(() => fischer.shop.isRetailConnectCart());

const showVariantImage = computed(() => Boolean(fields.showVariantImages?.value));

const showMemoListButton = computed(
  () =>
    props.shopType === 'closedShop' &&
    useMemoListFunction(fischer) &&
    Boolean(fischer.user.accessToken()) &&
    !fischer.app.isAppView(),
);

const memoListProduct = ref<string | null>(null);
const memoListQuantity = ref<number | null>(null);
const addToMemoListDialog =
  useTemplateRef<InstanceType<typeof AddToMemoListDialogFi>>('addToMemoListDialog');

const openMemoListDialog = ({
  productID,
  quantity,
}: {
  productID: string;
  quantity: number | null;
}) => {
  memoListProduct.value = productID;
  memoListQuantity.value = quantity;
  addToMemoListDialog.value?.show();
};

const onMemoListError = (message: string) => {
  console.error(message);
  openGeneralErrorNotification();
};

const catalogAttributes = computed(
  () => props.catalogData?.categoryAttributes || props.catalogData?.seriesAttributes || undefined,
);

const productListFilter = useTemplateRef<HTMLElement>('productListFilter');
const facetsMenu = useTemplateRef<any>('facetsMenu');

const createApolloVariables = () => {
  let id = fischer.catalogId('1001340182');
  let language = fischer.catalogLocale();
  let catalogLocale = fischer.catalogLocale();

  if (IS_SHOWROOM && props.devData) {
    id = props.devData.id;
    language = props.devData.language;
    catalogLocale = props.devData.language.replace('_', '-');
  }

  const extraFilters = withSalesChannelFilter([
    {
      associatedFieldName: 'Hidden_K',
      name: '!true',
    },
  ]);

  return {
    id,
    factFinderChannel: fischer.factFinderChannel(),
    channel: fischer.brandId(),
    brand: fischer.brandId(),
    baseSiteId: baseSiteId.value,
    language,
    hostname: fischer.hostname(),
    page: pagination.initial,
    quantity: pagination.pageSize,
    accessToken: fischer.user.accessToken(),
    userId: fischer.user.id(),
    filters: (autoFilters.value || filters.value).map(toFilterInput),
    extraFilters,
    supplier: supplier(),
    publicAssortment: isPublicAssortment(),
    siteSecure: fischer.isSecureConnection(),
    catalogLocale,
    maxHeadings: maxHeadings.value,
    noSitecoreLinks: true,
    useGtins: fischer.useGtins(),
    division: fischer.user.division(),
    distributionChannel: fischer.user.distributionChannel(),
    allFacets: fischer.isProductSeriesEnabled(),
    includeLogisticAttributes: fischer.shop.isClosedShop(),
  };
};

const collectFilterChanges = () => {
  const changes: any[] = [];
  const search = fischer.query();
  Object.entries(search ?? {}).forEach(([key, value]) => {
    if ((value as string).split(',').length > 1) return;

    const match = /filter(.*)/.exec(key);
    if (!match) return;

    const [, name] = match;

    if (/[^~]~~[^~]/.test(name)) {
      const [nameShort] = name.split('~~');
      const values = /^([0-9.]+)-([0-9.]+)/.exec(value as string);
      if (values) {
        changes.push({
          element: {
            name: nameShort,
            associatedFieldName: name,
            selectedMinValue: values[1],
            selectedMaxValue: values[2],
            selected: true,
          },
          value: true,
        });
        return;
      }
    }
    changes.push({
      element: {
        associatedFieldName: name,
        name: value,
        selected: true,
      },
      value: true,
    });
  });
  return changes;
};

// Apply the URL's page and facet filters before the search query exists. urql keys the SSR
// payload by the variables, so a first execution without them asks for a key the server never
// serialised: the client then hydrates with no data and the whole list mismatches.
const initialPage = Number(fischer.query('page')) || 1;
pagination.initial = initialPage;
pagination.page = initialPage;
applyChanges(collectFilterChanges());

// R5: main paginated search query
const searchPauseRef = computed(
  () => !(IS_SHOWROOM ? true : !!baseSiteId.value && !!fischer.catalogId()),
);
const searchVariables = computed(() => ({
  ...createApolloVariables(),
  page: pagination.page,
  ...(pagination.page > 1 && { includeUnselectedFacets: false }),
}));
const {
  data: searchData,
  fetching: searchFetching,
  executeQuery: executeSearchQuery,
} = useQuery<any, any>({
  query: SEARCH_PRODUCTS_FOR_FAMILY_NG,
  variables: searchVariables,
  pause: searchPauseRef,
});

const accumulatedHits = ref<any[]>([]);
// Dedupe by product ID, keeping first occurrence. Safe client-side guard (a product list must never
// show the same product twice), but a WORKAROUND: duplicates mean the GraphQL backend returned
// overlapping pages (page→offset off-by-one in fischer-frontend-graphql, see
// PAGINATION_DUPLICATE_BUG_HANDOFF.md). Log loudly so this is never silently masked.
const dedupeById = (products: any[]): any[] => {
  const seen = new Set<unknown>();
  const droppedIds: unknown[] = [];
  const result = products.filter((p) => {
    const id = p?.ID;
    if (id === undefined || id === null) return true;
    if (seen.has(id)) {
      droppedIds.push(id);
      return false;
    }
    seen.add(id);
    return true;
  });
  if (droppedIds.length) {
    console.error(
      `[ProductListViewFi] Dropped ${droppedIds.length} duplicate product(s) while accumulating ` +
        `paginated results (page=${pagination.page}). This means the GraphQL backend returned ` +
        'overlapping pages — the client dedupe is masking a backend pagination bug. Duplicate IDs:',
      droppedIds,
    );
  }
  return result;
};
// Declared above the watcher on purpose: the watcher is `immediate`, so it runs inside setup()
// and everything it calls has to be initialised by then, or it throws a TDZ ReferenceError.
const setLegend = () => {
  showLegend.value = false;
  products.value.forEach(({ catalogProductData: { Attributes } = {} as any }) => {
    (Attributes || []).forEach(({ AttributeKey }: any) => {
      if (props.approvals.includes(AttributeKey)) {
        showLegend.value = true;
      }
    });
  });
};

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: an SSR-restored result is delivered synchronously during setup (see
// createSsrExchange), so a lazy watcher would never see it - the list would stay empty with its
// loader up, and `ready` would never be emitted. No-ops while there is nothing to read.
watch(
  searchData,
  (d) => {
    if (!d?.searchProductsForFamilyNg) return;
    const hits = d.searchProductsForFamilyNg.hits.map((hit: any) => ({
      ...(hit.variantValuesObject ? hit.variantValuesObject[0] : hit),
    }));
    if (pagination.page <= (pagination.initial || 1)) {
      accumulatedHits.value = dedupeById(hits);
    } else {
      accumulatedHits.value = dedupeById([...accumulatedHits.value, ...hits]);
    }
    pagination.total = d.searchProductsForFamilyNg.totalHits;
    totalHits.value = d.searchProductsForFamilyNg.totalHits;
    products.value = accumulatedHits.value;
    (familyProducts as any).value = accumulatedHits.value;
    setLegend();
    isLoading.value = false;
    loadingQuantityInputs.value = {};
    isAddingItemToCart.value = {};
    emit('ready');
  },
  { immediate: true, flush: 'sync' },
);

onServerPrefetch(async () => {
  if (searchPauseRef.value) return;
  await executeSearchQuery();
});

// R1: base facets query (non-paginated, SEO-relevant)
const baseFacetsPauseRef = computed(() => !(!!baseSiteId.value && !!fischer.catalogId()));
const baseFacetsVariables = computed(() => {
  const vars = createApolloVariables();
  return {
    factFinderChannel: vars.factFinderChannel,
    language: vars.language,
    id: vars.id,
    baseSiteId: vars.baseSiteId,
    allFacets: fischer.isProductSeriesEnabled(),
  };
});
const { data: baseFacetsData, executeQuery: executeBaseFacetsQuery } = useQuery<any, any>({
  query: GET_FAMILY_BASE_FACETS_NG,
  variables: baseFacetsVariables,
  pause: baseFacetsPauseRef,
});

onServerPrefetch(async () => {
  if (baseFacetsPauseRef.value) return;
  await executeBaseFacetsQuery();
});

// R1: retailer availability query (non-paginated, auth-gated, client-only)
const { data: retailerData, fetching: retailerAvailabilityLoading } = useQuery<any, any>({
  query: GET_RETAILER_AVAILABILITY_FOR_IDS,
  variables: computed(() => ({
    baseSiteId: baseSiteId.value,
    productIds: productIds.value,
    useGtins: fischer.useGtins(),
  })),
  pause: computed(
    () => !(isMounted.value && isRetailConnectCart.value && productIds.value.length > 0),
  ),
});

const searchProductsForFamilyNg = computed(
  () => searchData.value?.searchProductsForFamilyNg ?? null,
);
const getFamilyBaseFacetsNg = computed(() => baseFacetsData.value?.getFamilyBaseFacetsNg ?? null);
const getRetailerAvailabilityForIds = computed(
  () => retailerData.value?.getRetailerAvailabilityForIds ?? [],
);

const productIds = computed(() => {
  return accumulatedHits.value.map((product: any) => product.ID);
});

const { bestPrices, loadingPrices } = useBestPrices(productIds);

const isProductIsLoading = computed(() => {
  if (!isMounted.value) return false;
  return searchFetching.value;
});

const retailerOutOfStock = computed(() =>
  (getRetailerAvailabilityForIds.value || []).map((availability: any) => {
    if (!availability) return false;
    return availability.stock === 'empty' || availability.stock === null;
  }),
);

const filterUrlParams = computed(() => {
  const search: string[] = [];
  filters.value.forEach((filter) => {
    search.push(
      `filter${encodeURIComponent(filter.associatedFieldName)}=${encodeURIComponent(
        stringifyFilterValue(filter),
      )}`,
    );
  });
  if (search.length === 0) return '';
  return `?${search.sort().join('&')}`;
});

const facetsTransformed = computed(() => {
  if (!searchProductsForFamilyNg.value) return [];
  return mergeFacets(
    searchProductsForFamilyNg.value.facetDataUnselected,
    searchProductsForFamilyNg.value.facets,
    getFamilyBaseFacetsNg.value || [],
    autoFilters.value || filters.value,
  );
});

const displayedItems = computed(() => {
  if (!searchProductsForFamilyNg.value) return 0;
  return Math.min(pagination.page * pagination.pageSize, searchProductsForFamilyNg.value.totalHits);
});

const pagingPercentage = computed(() => {
  if (!searchProductsForFamilyNg.value) return 0;
  return (100 * displayedItems.value) / searchProductsForFamilyNg.value.totalHits;
});

const facetsLoaded = computed(() => initDone.value && !!getFamilyBaseFacetsNg.value);

// Derived from the accumulated hits, NOT from searchProductsForFamilyNg.hits: the query result
// only ever holds the page that was fetched last, while the table keeps rendering every page
// loaded so far. Keying off the query result left all previously loaded rows without an entry, so
// clicking "load more" blanked their attributes (and with them the table head and approvals
// toggle, which read products[0]).
const paddedAttributes = computed(() =>
  padAttributes(accumulatedHits.value, catalogAttributes.value),
);

const hasApprovalAttributes = computed(() => {
  if (!products.value?.length) return false;
  const attributes = paddedAttributes.value[products.value[0]?.ID];
  if (!attributes || !props.approvals) return false;
  return attributes.some((a) => props.approvals.includes(a.AttributeKey));
});

const cleanName = (name: string) => factFinderNgNameCleaner(name);

const updateQueries = () => {
  if (!initDone.value) return;
  pagination.initial = 1;
  pagination.page = 1;
  count.value += 1;
  // Do NOT clear accumulatedHits here. A filter change that hits the urql cache delivers its
  // result synchronously (flush:'sync' watcher) while applyChanges() is still running, i.e. BEFORE
  // this runs - the wipe would then throw that result away and no further update ever arrives,
  // leaving the rendered rows (productListStore.products) without attributes or prices. The
  // watcher replaces accumulatedHits wholesale on page 1 anyway.
};

const pushUriState = () => {
  if (IS_SSR) return;

  const customQuery: string[] = [];
  const query = fischer.query();
  const filterKeys = ['filter', 'query'];
  Object.entries(query ?? {}).forEach(([key, value]) => {
    const values = (value as string).split(',');
    if (values.length > 1) return;
    if (!filterKeys.some((el) => key.startsWith(el))) {
      customQuery.push(`${key}=${encodeURIComponent(value as string)}`);
    }
  });

  if (!filterUrlParams.value && !customQuery.length && !window.location.search) return;

  let state = filterUrlParams.value;
  if (customQuery.length) {
    if (filterUrlParams.value) {
      state = `${filterUrlParams.value}&${customQuery.join('&')}`;
    } else {
      state = `?${customQuery.join('&')}`;
    }
  }

  window.history.replaceState('', '', state || '?');
};

const changeFacet = (...changes: any[]) => {
  applyChanges(changes);
  if (!useAutoFilters.value) {
    updateQueries();
    pushUriState();
  }
};

const jumpToProductList = () => {
  if (IS_SSR || isSM.value || !componentRoot.value) return;
  smoothScrollToElement(componentRoot.value, { behavior: 'instant' });
};

const clearAllFacets = () => {
  enableAutoFilters();
  filters.value = [];
  if (initDone.value) {
    updateQueries();
    pushUriState();
  }
};

// The URL state is already applied above, before the search query is created.
initDone.value = true;
if (!useAutoFilters.value) {
  pushUriState();
}

const productInCart = (item: any) => {
  if (cart.value?.products) {
    return cart.value.products.some(
      (product) => product?.articleNo === item.catalogProductData.ProductId,
    );
  }
  return false;
};

const addProduct = async (data: AddProductToCartParameters) => {
  const product = products.value.find((p: any) => p.ID === data.productID);
  if (product) {
    delete product.quantityIncreasedMessage;
  }
  addProductToCart(data);
};

const removeQuantityHints = () => {
  products.value.forEach((p: any) => {
    delete p.quantityIncreasedMessage;
  });
};

const getAttributes = (attributes: any[] | undefined) => {
  if (!attributes) return [];
  const attributesWithoutApprovals = attributes.filter(
    (a) => !props.approvals.includes(a.AttributeKey),
  );
  const allAttributes = approvalsEnabled.value ? attributes : attributesWithoutApprovals;
  return allAttributes.slice(0, maxHeadings.value);
};

const fetchMoreProducts = () => {
  // R5: increment page — urql auto-re-runs via searchVariables computed
  pagination.page = pagination.page + 1;
};

const goToFilter = () => {
  if (!isSM.value) {
    facetsMenu.value?.openMobileFacets();
    return;
  }
  if (productListFilter.value) {
    smoothScrollToElement(productListFilter.value, {
      duration: 300,
      offset: 80,
    });
  } else {
    console.error('can not find filter to scroll to');
  }
};

const onIntersect = (observer: any) => {
  if (
    IS_SSR ||
    window.scrollY === 0 ||
    !observersReady.value ||
    isLoading.value ||
    isProductIsLoading.value
  )
    return;

  if (!observer?.[0]?.isIntersecting && observer[0]?.target?.getBoundingClientRect?.()?.top <= 0) {
    isFilterOutOfView.value = true;
    showFilterBadge.value = true;
  } else {
    isFilterOutOfView.value = false;
    showFilterBadge.value = false;
  }
};

const onIntersectFilterOut = (observer: any) => {
  if (
    IS_SSR ||
    window.scrollY === 0 ||
    !observersReady.value ||
    isLoading.value ||
    isProductIsLoading.value
  )
    return;

  if (!observer?.[0]?.isIntersecting && observer[0]?.target?.getBoundingClientRect?.()?.top <= 0) {
    showFilterBadge.value = false;
  } else if (isFilterOutOfView.value) {
    showFilterBadge.value = true;
  }
};

watch(facetsLoaded, (value) => {
  if (!value) return;
  filters.value.forEach((activeFilter) => {
    const baseFilter = getFamilyBaseFacetsNg.value?.find(
      (baseFacet: any) => baseFacet.associatedFieldName === activeFilter.associatedFieldName,
    );
    const firstElement = baseFilter?.elements?.[0];
    if (
      !firstElement ||
      (firstElement.absoluteMinValue !== null &&
        firstElement.absoluteMinValue !== undefined &&
        firstElement.absoluteMinValue === firstElement.absoluteMaxValue)
    ) {
      changeFacet({
        value: false,
        element: {
          selected: false,
          name: activeFilter.name,
          associatedFieldName: activeFilter.associatedFieldName,
        },
      });
    }
  });
});

watch(
  activeFacets,
  () => {
    changeFacet();
  },
  { deep: true },
);

watch(
  isProductIsLoading,
  (value) => {
    loading.value = value;
  },
  { immediate: true },
);

watch(
  facetsTransformed,
  (value) => {
    if (!searchProductsForFamilyNg.value) return;
    setFamilyFacets(value, props.catalogData);
  },
  { deep: true },
);

defineExpose({ changeFacet, clearAllFacets });

onMounted(() => {
  const storedApprovalsEnabled = localStorage.getItem('productList:approvalsEnabled');
  if (storedApprovalsEnabled === 'true') {
    approvalsEnabled.value = true;
  }
  if (fischer.cartId() && (fischer.shop.isClosedShop() || isRetailConnectCart.value)) {
    getCart(false, true);
  }
  isMounted.value = true;
  // The facetsTransformed watcher never fires when the search data is already restored from the SSR state,
  // so the facets have to be pushed into the store once here.
  if (searchProductsForFamilyNg.value) {
    setFamilyFacets(facetsTransformed.value, props.catalogData);
  }
  nextTick(() => {
    observersReady.value = true;
  });
});
</script>

<style scoped src="./product-list-view-fi" />
